import { SkeletonText } from '@dsx-react/skeleton';
import { classNames } from '@dsx-react/util';
import { Fragment } from 'react';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "@supports (display: grid) {\n  .Table-module_dl__Er7xD {\n    display: grid;\n    grid-template-columns: min-content auto;\n  }\n}\n.Table-module_dt__jcgQf {\n  margin: 0;\n  padding: var(--size-300);\n  white-space: nowrap;\n  font-weight: bold;\n}\n\n.Table-module_dd__zl5FT {\n  margin: 0;\n  padding: var(--size-300);\n}\n\n.Table-module_dl__Er7xD + .Table-module_dl__Er7xD {\n  border-top: 2px solid var(--color-gray-100);\n}\n\n.Table-module_table-scroller__Cttim {\n  overflow: auto;\n  position: relative;\n  isolation: isolate;\n}\n\n.Table-module_table__4BALz {\n  width: 100%;\n  border-collapse: collapse;\n}\n\n.Table-module_row__qI3eu {\n  background-color: var(--color-neutral-0);\n}\n\n.Table-module_row__qI3eu:nth-child(2n) {\n  background-color: var(--color-neutral-50);\n}\n\n.Table-module_row__qI3eu + .Table-module_row__qI3eu {\n  border-top: 2px solid var(--color-gray-100);\n}\n\n.Table-module_thead__d-qwH {\n  background-color: var(--color-gray-100);\n}\n\n.Table-module_sticky-top__PQ5uI {\n  position: sticky;\n  top: 0;\n  z-index: 1;\n}\n\n.Table-module_th__CdpZB {\n  padding: var(--size-300);\n}\n\n.Table-module_td__hkaNh {\n  padding: var(--size-300);\n}\n\n.Table-module_align-start__imQEB {\n  text-align: left;\n}\n\n.Table-module_align-center__WxoSo {\n  text-align: center;\n}\n\n.Table-module_align-end__oTm7E {\n  text-align: right;\n}\n\n.Table-module_valign-start__YpgAE {\n  vertical-align: top;\n}\n\n.Table-module_valign-center__00H7Q {\n  vertical-align: middle;\n}\n\n.Table-module_valign-end__sFVwN {\n  vertical-align: bottom;\n}";
var styles = {"dl":"Table-module_dl__Er7xD","dt":"Table-module_dt__jcgQf","dd":"Table-module_dd__zl5FT","table-scroller":"Table-module_table-scroller__Cttim","table":"Table-module_table__4BALz","row":"Table-module_row__qI3eu","thead":"Table-module_thead__d-qwH","sticky-top":"Table-module_sticky-top__PQ5uI","th":"Table-module_th__CdpZB","td":"Table-module_td__hkaNh","align-start":"Table-module_align-start__imQEB","align-center":"Table-module_align-center__WxoSo","align-end":"Table-module_align-end__oTm7E","valign-start":"Table-module_valign-start__YpgAE","valign-center":"Table-module_valign-center__00H7Q","valign-end":"Table-module_valign-end__sFVwN"};
styleInject(css_248z);

/**
 * A lightweight table for displaying a simple set of data. The columns can be customized a bit.
 */
function Table(props) {
  const {
    layout = 'table'
  } = props;
  return layout === 'table' ? /*#__PURE__*/jsx(TableFull, {
    ...props
  }) : /*#__PURE__*/jsx(TableDataList, {
    ...props
  });
}
function TableDataList(props) {
  const {
    className: callerClassName,
    columns,
    loadingState,
    rowKey,
    rows,
    skeletonRows = 3,
    tag
  } = props;
  const className = classNames('dsx-component', styles['dl'], callerClassName);
  const isLoading = loadingState === 'loading';
  const usePlaceholderRows = !rows.length && isLoading;
  const rowsOrPlaceholders = usePlaceholderRows ? [...Array(skeletonRows)] : rows;
  const rowEls = rowsOrPlaceholders.map((row, i) => {
    const keyForRow = usePlaceholderRows ? i : rowKey(row);
    const tagForRow = usePlaceholderRows ? null : keyForRow;
    const colEls = columns.map(col => {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx("dt", {
          className: styles['dt'],
          "data-dsx--table--heading": col.key,
          children: col.heading
        }), /*#__PURE__*/jsx("dd", {
          className: styles['dd'],
          "data-dsx--table--value": col.key,
          children: /*#__PURE__*/jsx(TableValue, {
            row: row,
            col: col,
            isLoading: isLoading
          })
        })]
      }, col.key);
    });
    return /*#__PURE__*/jsx("dl", {
      className: className,
      "aria-live": "polite",
      "aria-atomic": "true",
      "data-dsx--table--row": tagForRow,
      children: colEls
    }, keyForRow);
  });
  return /*#__PURE__*/jsx("section", {
    "data-dsx--table": tag,
    children: rowEls
  });
}
function TableFull(props) {
  const {
    rows,
    rowKey,
    columns,
    tag,
    loadingState,
    skeletonRows = 5,
    className: callerClassName
  } = props;
  const className = classNames('dsx-component', styles['table-scroller'], callerClassName);
  const isLoading = loadingState === 'loading';
  const ths = columns.map(col => {
    const {
      key,
      heading,
      textAlign = 'start'
    } = col;
    const thClassName = classNames(styles['th'], styles[`align-${textAlign}`]);
    return /*#__PURE__*/jsx("th", {
      "data-dsx--table--heading": key,
      className: thClassName,
      children: heading
    }, key);
  });
  const usePlaceholderRows = !rows.length && isLoading;
  const rowsOrPlaceholders = usePlaceholderRows ? [...Array(skeletonRows)] : rows;
  const trs = rowsOrPlaceholders.map((row, i) => {
    const keyForRow = usePlaceholderRows ? i : rowKey(row);
    const tagForRow = usePlaceholderRows ? null : keyForRow;
    const tds = columns.map(col => {
      const {
        key,
        textAlign = 'start',
        verticalAlign = 'center'
      } = col;
      const tdClassName = classNames(styles['td'], styles[`align-${textAlign}`], styles[`valign-${verticalAlign}`]);
      return /*#__PURE__*/jsx("td", {
        "data-dsx--table--value": key,
        className: tdClassName,
        children: /*#__PURE__*/jsx(TableValue, {
          row: row,
          col: col,
          isLoading: isLoading
        })
      }, key);
    });
    return /*#__PURE__*/jsx("tr", {
      "data-dsx--table--row": tagForRow,
      className: styles['row'],
      children: tds
    }, keyForRow);
  });
  return /*#__PURE__*/jsx("div", {
    className: className,
    children: /*#__PURE__*/jsxs("table", {
      className: styles['table'],
      "data-dsx--table": tag,
      children: [/*#__PURE__*/jsx("thead", {
        className: classNames(styles['sticky-top'], styles['thead']),
        "data-dsx--table--thead": tag,
        children: /*#__PURE__*/jsx("tr", {
          children: ths
        })
      }), /*#__PURE__*/jsx("tbody", {
        "data-dsx--table--tbody": tag,
        "aria-live": "polite",
        "aria-atomic": "true",
        children: trs
      })]
    })
  });
}
function TableValue(props) {
  const {
    col,
    row,
    isLoading
  } = props;
  if (isLoading) {
    return /*#__PURE__*/jsx(SkeletonText, {});
  }
  if (col.render) {
    return /*#__PURE__*/jsx(Fragment$1, {
      children: col.render(row)
    });
  }
  if (row != null && typeof row === 'object' && col.key in row) {
    return /*#__PURE__*/jsx(Fragment$1, {
      children: row[col.key]
    });
  }
  return null;
}

export { Table };
